import toast from 'react-hot-toast';
import { format, isValid } from 'date-fns';

/**
 * Handles API errors by parsing field-specific errors or general messages
 * and displaying them using toast notifications.
 *
 * @param {object} err - The error object returned from the API call.
 * @param {string} defaultMessage - The default message to display if no specific error message is found.
 */
export function handleApiError(err, defaultMessage = 'An error occurred.') {
  const errorData = err.response?.data;

  if (Array.isArray(errorData)) {
    errorData.forEach((msg) => {
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    });
  } else if (errorData && typeof errorData === 'object' && !errorData.message) {
    Object.keys(errorData).forEach((key) => {
      const errors = errorData[key];
      const fieldName =
        key === 'non_field_errors' || key === 'detail'
          ? ''
          : `${key.charAt(0).toUpperCase() + key.slice(1)}: `;

      if (Array.isArray(errors)) {
        errors.forEach((msg) => {
          toast.error(`${fieldName}${msg}`);
        });
      } else if (typeof errors === 'string') {
        toast.error(`${fieldName}${errors}`);
      }
    });
  } else {
    const msg = errorData?.message || errorData?.detail || defaultMessage;
    toast.error(msg);
  }
}

/**
 * Parses API error responses into a standardized structure with a generic message
 * and a map of specific field errors, suitable for form validation.
 *
 * @param {object} err - The error object from axios/api client.
 * @returns {{ message: string, fieldErrors: Record<string, string> }}
 */
export function parseApiError(err) {
  const status = err?.response?.status;

  // If it's a 500+ error, return a static message
  if (status && status >= 500) {
    return {
      message: 'Internal Server Error. Please try again later.',
      fieldErrors: {},
    };
  }

  const errorData = err?.response?.data;
  if (!errorData) {
    if (err instanceof Error && err.message && !err.isAxiosError) {
      return { message: err.message, fieldErrors: {} };
    }
    return { message: 'An unexpected error occurred.', fieldErrors: {} };
  }

  let message = 'Failed to process request.';
  const fieldErrors = {};

  if (typeof errorData === 'object') {
    if (errorData.non_field_errors) {
      message = Array.isArray(errorData.non_field_errors)
        ? errorData.non_field_errors[0]
        : errorData.non_field_errors;
    } else if (errorData.message) {
      message = errorData.message;
    } else if (errorData.detail) {
      message = errorData.detail;
    } else {
      const keys = Object.keys(errorData).filter((k) => k !== '0' && isNaN(k));
      if (keys.length > 0) {
        const firstVal = errorData[keys[0]];
        message = Array.isArray(firstVal)
          ? firstVal[0]
          : typeof firstVal === 'string'
            ? firstVal
            : 'Invalid data provided.';
      } else if (errorData['0']) {
        message = errorData['0'];
      }
    }

    Object.entries(errorData).forEach(([key, val]) => {
      if (
        key !== 'non_field_errors' &&
        key !== 'message' &&
        key !== 'detail' &&
        key !== 'code' &&
        isNaN(key)
      ) {
        fieldErrors[key] = Array.isArray(val) ? val[0] : val;
      }
    });

    if (
      errorData.detail &&
      (errorData.code === 'invalid phone number' ||
        errorData.code === 'invalid_phone_number')
    ) {
      fieldErrors.phone = errorData.detail;
    }
  } else if (typeof errorData === 'string') {
    message = errorData;
  }

  return { message, fieldErrors };
}

/**
 * Formats a name string to Title Case.
 * Handles multiple spaces, empty values, and hyphens.
 *
 * @param {string} value - The name string to format.
 * @returns {string} The title-cased name string.
 */
export const toTitleCase = (value = '') => {
  if (!value) return '';

  return value
    .toString()
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((word) =>
      word
        .split('-')
        .map((part) =>
          part
            ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
            : '',
        )
        .join('-'),
    )
    .join(' ');
};

/**
 * Formats a name string according to specific rules:
 * - Title Case for normal words.
 * - Preserves fully uppercase words if they have at least two alphabetic characters.
 * - Removes unnecessary leading, trailing, and duplicate spaces.
 *
 * @param {string} value - The name string to format.
 * @returns {string} The formatted name string.
 */
export const formatName = (value = '') => {
  if (!value) return '';

  return value
    .toString()
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((word) => {
      const hasTwoAlpha = (word.match(/[a-zA-Z]/g) || []).length >= 2;
      const isAllUpper = word === word.toUpperCase();

      if (hasTwoAlpha && isAllUpper) {
        return word;
      }

      return word
        .split('-')
        .map((part) =>
          part
            ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
            : '',
        )
        .join('-');
    })
    .join(' ');
};

/**
 * Formats a consent status string for UI display.
 * e.g., 'opt_in' -> 'Opt In'
 *
 * @param {string} status - The consent status string
 * @returns {string} The formatted string
 */
export const formatConsentStatus = (status = '') => {
  if (!status) return '-';
  const lowerStatus = String(status).toLowerCase().trim().replace(/\s+/g, '_');
  if (lowerStatus === 'opt_in' || lowerStatus === 'optin') return 'Opt In';
  if (lowerStatus === 'opt_out' || lowerStatus === 'optout') return 'Opt Out';
  if (lowerStatus === 'unknown') return 'Unknown';
  return toTitleCase(String(status).replace(/_/g, ' '));
};

/**
 * Extract a clean single-character initial for user avatars.
 * Normalizes stylized fonts (NFKC), skips leading symbols/punctuations/emojis,
 * and returns uppercase letter/digit or fallback.
 *
 * @param {string} name - The user's name, username, or phone/identifier.
 * @returns {string} Single uppercase character initial.
 */
export const getAvatarInitial = (name = '') => {
  if (!name || typeof name !== 'string') return '?';

  // 1. Normalize unicode (e.g. 𝙺 -> K, ᴀ -> A, etc.)
  const normalized = name.normalize('NFKC').trim();

  // 2. Find first alphanumeric character (letter or digit across any script)
  const match = normalized.match(/[\p{L}\p{N}]/u);
  if (match) {
    return match[0].toUpperCase();
  }

  // 3. Fallback: first non-whitespace character
  const trimmed = normalized.replace(/^\s+/, '');
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
};

/**
 * Checks if a conversation window (24h Meta customer window) is active.
 *
 * @param {object} conv - The conversation object
 * @returns {boolean} Whether the conversation 24h window is active
 */
/**
 * Decode common HTML entities to characters. Result is plain text; callers
 * that inject HTML must escape again after this.
 */
function entityToChar(codePoint) {
  if (!Number.isInteger(codePoint) || codePoint < 0 || codePoint > 0x10ffff) {
    return null;
  }
  return String.fromCodePoint(codePoint);
}

export function decodeHtmlEntities(value) {
  if (value == null) return '';
  return String(value).replace(
    /&(?:#(\d+)|#x([0-9a-f]+)|quot|apos|#39|lt|gt|nbsp|amp);/gi,
    (entity, decimal, hex) => {
      if (decimal) return entityToChar(Number(decimal)) ?? entity;
      if (hex) return entityToChar(parseInt(hex, 16)) ?? entity;
      const name = entity.slice(1, -1).toLowerCase();
      if (name === 'quot') return '"';
      if (name === 'apos' || name === '#39') return "'";
      if (name === 'lt') return '<';
      if (name === 'gt') return '>';
      if (name === 'nbsp') return ' ';
      if (name === 'amp') return '&';
      return entity;
    },
  );
}

/**
 * Formats a date string or Date object to "DD MMM YYYY, HH:MM AM/PM"
 * e.g., "09 Oct 2026, 03:57 PM"
 *
 * @param {string | Date | number} value - The date to format
 * @param {string} [fallback='—'] - Fallback string if date is null/invalid
 * @returns {string} Formatted date string
 */
export function formatDateTime(value, fallback = '—') {
  if (!value) return fallback;
  const date = value instanceof Date ? value : new Date(value);
  if (!isValid(date) || Number.isNaN(date.getTime())) return fallback;
  return format(date, 'dd MMM yyyy, hh:mm a');
}

/**
 * Expired WhatsApp window stamp: 15 Oct 2026 02:38 PM in the browser timezone.
 * Empty string for null, undefined, or invalid dates.
 */
export function formatWindowExpiry(value) {
  if (value == null || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (!isValid(date)) return '';
  return format(date, 'dd MMM yyyy hh:mm a');
}

export const checkIsWindowActive = (conv) => {
  if (!conv) return true;
  if (conv.is_window_active !== undefined && conv.is_window_active !== null) {
    return Boolean(conv.is_window_active);
  }
  const lastTime =
    conv.last_message_at ||
    conv.last_inbound_message_at ||
    conv.last_message?.timestamp ||
    conv.created_at;

  if (!lastTime) return true;

  try {
    const diffMs = Date.now() - new Date(lastTime).getTime();
    if (isNaN(diffMs)) return true;
    const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
    return diffMs < TWENTY_FOUR_HOURS;
  } catch {
    return true;
  }
};
